import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { Link } from "src/components/routing";
import {
    GroupTrialPersonnelsBreadcrumbs,
    TrialGroupPersonnelsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialSubMenu,
    TrialGroupSubMenu,
} from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialGroupPersonnelsPage.scss";

interface TrialGroupPersonnelsPageProps {
    trialGroup: Dtos.TrialGroup;
    loadingTrialGroup: boolean;
    loadTrialGroupSuccess: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    trialPersonnel: Dtos.TrialPersonnel[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelsLoadByTrialGroupIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialGroupPersonnelsPage: FC<TrialGroupPersonnelsPageProps> = ({
    trialGroup,
    loadingTrialGroup,
    loadTrialGroupSuccess,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    trialPersonnel,
    loadingTrialPersonnel,
    loadTrialPersonnelSuccess,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    permissions,
    clearTrialGroup,
    loadTrialGroup,
    clearTrialPersonnel,
    loadTrialPersonnel,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const { trialId } = useParams<{ trialId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);
    const trialIdNumber = parseInt(trialId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- Variables --
    const showTrialPersonnels =
        !loadingTrialGroup && 
        loadTrialGroupSuccess && 
        !loadingTrialPersonnel &&
        loadTrialPersonnelSuccess &&
        !creatingTree;


    // Load trial group
    useEffect(() => {
        if (
            groupIdNumber &&
            trialIdNumber &&
            trialGroupIdNumber &&
            !trialGroup
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupPersonnelsBreadcrumbs
                    : GroupTrialPersonnelsBreadcrumbs;
            const drawerItemSetter =
                baseRoute === "trials" ? TrialGroupSubMenu : GroupTrialSubMenu;

            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber
                )
            );
            setDrawerItems(
                drawerItemSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    permissions
                )
            );

            setTreeData(null);
            loadTrialGroup(trialGroupIdNumber);
            loadTrialPersonnel(trialGroupIdNumber, undefined);
        }
    }, [
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        trialGroup,
        location,
        TrialGroupPersonnelsBreadcrumbs,
        GroupTrialPersonnelsBreadcrumbs,
        TrialGroupSubMenu,
        GroupTrialSubMenu,
        setDrawerItems,
        setBreadcrumbs,
        setTreeData,
        loadTrialGroup,
        permissions,
        loadTrialPersonnel,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
    }, [
        loadTrialGroupFailure,
        loadTrialGroupErrorCode,
        loadTrialPersonnelFailure,
        loadTrialPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            groupIdNumber &&
            trialIdNumber &&
            trialGroupIdNumber &&
            trialGroup?.group?.name &&
            trialGroup?.trial?.studyAcronym
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupPersonnelsBreadcrumbs
                    : GroupTrialPersonnelsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    trialGroup?.group?.name,
                    trialGroup?.trial?.studyAcronym
                )
            );
        }
    }, [
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        trialGroup,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial personnels & group on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrialGroup();
            clearTrialPersonnel();
        };
    }, [clearTrialGroup, clearTrialPersonnel, setTreeData]);

    // Refresh tree
    useEffect(() => {
        if (trialPersonnel) {
            setInitalizedTreeData(false);
        }
    }, [trialPersonnel, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (trialPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial group personnel return
            if (trialPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            let uniqueRoles: Dtos.Role[] = [];
            if (showCeased) {
                uniqueRoles = Array.from(
                    new Map(
                        trialPersonnel?.map((x) => [x.role.id, x.role])
                    ).values()
                );
            } 
            else {
                uniqueRoles = Array.from(
                    new Map(
                        trialPersonnel
                            ?.filter((x) => !x.dateCeased)
                            .map((x) => [x.role.id, x.role])
                    ).values()
                );
            }

            const createChildNodes = (roleId, showCeased) => {
                let children = [];

                if (showCeased) {
                    children =
                        trialPersonnel
                            ?.filter((y) => y.roleId == roleId)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y.id}`,
                                data: (
                                    <div
                                        className={`form-row ${
                                            y?.dateCeased ? "ceased" : ""
                                        }`}
                                    >
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                } 
                else {
                    children =
                        trialPersonnel
                            ?.filter((y) => y.roleId == roleId && !y.dateCeased)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y?.id}`,
                                data: (
                                    <div className="form-row">
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                }

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                uniqueRoles
                    ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                    ?.map((x) => ({
                        id: `role-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {
                                            trialPersonnel?.filter((y) =>
                                                showCeased
                                                    ? y.roleId == x.id
                                                    : y.roleId == x.id &&
                                                        !y.dateCeased
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.id, showCeased)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            // const expanded = treeNodes.map((z) => z.id);

            // setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialPersonnelRoleView
                    )
                ) {
                    const baseRoute = location?.pathname?.split("/")[1];

                    const url: string =
                        baseRoute === "trials"
                            ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/personnel/${id.replace(
                                    "personnel-",
                                    ""
                                )}/details`
                            : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/personnel/${id.replace(
                                    "personnel-",
                                    ""
                                )}/details`;
                    navigate(url);
                }
                if (id.includes("role")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            permissions,
            trialIdNumber,
            groupIdNumber,
            trialGroupIdNumber,
            location,
        ]
    );

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    const getCreateUrl = (
        groupId: number,
        trialId: number,
        trialGroupId: number
    ): string => {
        const baseRoute = location?.pathname?.split("/")[1];

        const url: string =
            baseRoute === "trials"
                ? `/trials/${trialId}/groups/${groupId}/trialgroup/${trialGroupId}/personnel/create`
                : `/groups/${groupId}/trials/${trialId}/trialgroup/${trialGroupId}/personnel/create`;
        return url;
    };

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-auto mb-2">
                        <h1>Personnel</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={onChange}
                            labelA="Hide ceased personnel"
                            labelB="Show ceased personnel"
                            style={{ minWidth: "200px" }}
                        />
                        <a
                            className="btn btn-info mr-2"
                            target="_blank"
                            href={encodeURI(
                                `/api/personnelrole/export/${trialIdNumber}/0/${groupIdNumber}/0`
                            )}
                        >
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                        </a>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialPersonnelRoleCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={getCreateUrl(
                                    groupIdNumber,
                                    trialIdNumber,
                                    trialGroupIdNumber
                                )}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialPersonnels ? (
                    <div className="d-flex w-100 justify-content-center pb-3">
                        <InlineLoading
                            size={34}
                            text={""}
                            absoluteCenter={false}
                        />
                    </div>
                ) : null}
                {showTrialPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no trial group
                                        personnel
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        trialGroup,
        showCeased,
        onChange,
        showTrialPersonnels,
        treeData,
        permissions,
        getCreateUrl,
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        handleToggle,
        toggleState,
    ]);
    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialPersonnelRoleView}
            loading={loadingTrialGroup || loadingTrialPersonnel}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupPersonnelsPageProps> => {
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let trialPersonnel: Dtos.TrialPersonnel[] | undefined = (state.trialPersonnel.formData instanceof Array)
        ? state.trialPersonnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroup: trialGroup!,
        loadingTrialGroup:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupSuccess:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
        trialPersonnel: trialPersonnel!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupPersonnelsPageProps> => {
    return {
        loadTrialGroup: bindActionCreators(
            TrialGroupActions.LoadTrialGroupById,
            dispatch
        ),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch),
        loadTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.LoadTrialPersonnelsByTrialGroupId,
            dispatch
        ),
        clearTrialPersonnel: bindActionCreators(TrialPersonnelActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupPersonnelsPage);
